<template>
  <div class="tickets-page">
    <div class="page-header">
      <h2>{{ t('Meus Chamados de Suporte') }}</h2>
      <div class="header-actions">
        <Button
          :label="t('Novo Chamado')"
          icon="pi pi-plus"
          severity="primary"
          data-testid="support-new-ticket-button"
          @click="openNewTicketModal"
        />
        <Button
          :label="t('Atualizar')"
          icon="pi pi-refresh"
          severity="secondary"
          outlined
          :loading="isLoading"
          data-testid="support-refresh-tickets-button"
          @click="refreshTickets"
        />
      </div>
    </div>

    <tickets-table
      ref="ticketsTableRef"
      @view-ticket="handleViewTicket"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useOdooTicket } from '../../composables/useOdooTicket';
import { log } from '../../utils/DebugLogger';
import TicketsTable from './TicketsTable.vue';

interface TicketsTableExpose {
  loadData: () => Promise<void> | void;
}

interface TicketRow {
  id: number | string;
  [key: string]: unknown;
}

const { t } = useI18n();
const { isLoading } = useOdooTicket();

const ticketsTableRef = ref<TicketsTableExpose | null>(null);

const openNewTicketModal = (): void => {
  log('TicketsPage', 'openNewTicketModal', 'Opening new ticket modal');
  eventBus.emit('modal.support-ticket.open');
};

const refreshTickets = (): void => {
  log('TicketsPage', 'refreshTickets', 'Refreshing tickets list');
  if (ticketsTableRef.value) {
    void ticketsTableRef.value.loadData();
  }
};

const handleViewTicket = (ticket: TicketRow): void => {
  log('TicketsPage', 'handleViewTicket', 'Opening ticket detail', ticket);
  eventBus.emit('modal.ticket-detail.open', { ticket });
};

const handleTicketCreated = (): void => {
  log('TicketsPage', 'handleTicketCreated', 'Ticket created, refreshing list');
  refreshTickets();
};

onMounted(() => {
  log('TicketsPage', 'mounted', 'Component mounted');
  eventBus.on('ticket.created', handleTicketCreated);
});

onBeforeUnmount(() => {
  eventBus.off('ticket.created', handleTicketCreated);
});
</script>

<style scoped>
.tickets-page {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-color);
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
